import { FadeInOutVariants } from '@/constants/animations';
import { useHeaderHeight } from '@/hooks/useHeaderHeight';
import DashboardLayout from '@/layouts/Dashboard';
import FullCaptureListFAB from '@/pages/WeeklyCapturePage/Sections/FullCaptureListFAB';
import { RoutesList } from '@/routes/routesList';
import { useCategories } from '@/services/categories/hooks';
import { useActionsCategories } from '@/stores/useActionsCategories';
import { Category } from '@/types/category';
import rem from '@/utils/rem';
import { Box, Center, Flex, Spinner, useDisclosure } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import CategorySidebar from './components/CategorySidebar';
import ActionsSection from './sections/ActionsSection';
import HeroSection from './sections/HeroSection';
import ProjectsSection from './sections/ProjectsSection';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const CategorySpotlight: React.FC = () => {
  const { t } = useTranslation('CategorySpotlight');
  const { t: tWeeklyCapture } = useTranslation('WeeklyCapturePage');
  const selectedCategory = useActionsCategories((state) => state.selectedCategory);
  const { data: categoriesList } = useCategories();
  const headerHeight = useHeaderHeight();
  const navigate = useNavigate();
  // Loading state for hero image
  const [isHeroImageLoaded, setIsHeroImageLoaded] = useState(false);
  const [currentCategoryId, setCurrentCategoryId] = useState<string | null>(null);

  // Get full category data from backend based on selected category ID
  const fullCategoryData = useMemo(
    () => categoriesList?.category?.find((el: Category) => el?.id === selectedCategory?.id),
    [categoriesList?.category, selectedCategory?.id],
  );
  const { isOpen: isSidebarOpen, onOpen: onSidebarOpen, onClose: onSidebarClose } = useDisclosure();

  // Reset loading state when category changes
  useEffect(() => {
    const newCategoryId = fullCategoryData?.id || null;

    if (newCategoryId !== currentCategoryId) {
      // Category changed, reset loading state

      setIsHeroImageLoaded(false);
      setCurrentCategoryId(newCategoryId);

      // Safety timeout: if image doesn't load in 3 seconds, show content anyway
      const timeoutId = setTimeout(() => {
        setIsHeroImageLoaded(true);
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [fullCategoryData?.id, currentCategoryId, isHeroImageLoaded]);

  // Handler for when hero image finishes loading
  const handleHeroImageLoad = useCallback(() => {
    const targetCategoryId = fullCategoryData?.id;

    // Only set loaded if this callback is for the current category
    if (targetCategoryId === currentCategoryId) {
      setIsHeroImageLoaded(true);
    }
    // Ignore callbacks from previous categories
  }, [fullCategoryData?.id, currentCategoryId]);

  return (
    <DashboardLayout pageTitle={t('pageTitle')} padding="0">
      <Flex position="relative" height={`calc(100vh - ${rem(headerHeight)})`} paddingLeft={rem(25)}>
        {/* Category Sidebar */}
        <CategorySidebar isOpen={isSidebarOpen} onClose={onSidebarClose} />

        {/* Main Content */}
        <Flex flexDirection="column" flex="1" overflowY="auto">
          {!selectedCategory || !fullCategoryData ? (
            /* Empty state */
            <Flex alignItems="center" justifyContent="center" flex="1">
              <Box color="text-secondary" textAlign="center">
                {t('emptyState.title')}
              </Box>
            </Flex>
          ) : (
            <MotionFlex
              key={fullCategoryData.id} // Force re-animation when category changes
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={FadeInOutVariants}
              transition={{ duration: 0.3 }}
              flexDirection="column"
              width="full"
            >
              {/* Loading Overlay */}
              {!isHeroImageLoaded && (
                <Center
                  position="fixed"
                  zIndex="9999"
                  top="0"
                  right="0"
                  bottom="0"
                  left="0"
                  backgroundColor="background-primary"
                >
                  <Spinner
                    color="cyan.400"
                    emptyColor="rgba(255, 255, 255, 0.2)"
                    size="xl"
                    speed="0.65s"
                    thickness="4px"
                  />
                </Center>
              )}

              {/* Hero Section */}
              <MotionBox
                flexShrink={0}
                initial="hidden"
                animate={isHeroImageLoaded ? 'visible' : 'hidden'}
                variants={FadeInOutVariants}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <HeroSection category={fullCategoryData} onImageLoad={handleHeroImageLoad} />
              </MotionBox>

              {/* Actions and Projects sections */}
              <MotionFlex
                flexDirection={{ base: 'column', md: 'column', lg: 'row', '3xl': 'row' }}
                gap={{ base: 0, md: 0, lg: 0, '3xl': 0 }}
                minHeight="fit-content"
                marginTop={rem(50)}
                marginBottom={rem(40)}
                initial="hidden"
                animate={isHeroImageLoaded ? 'visible' : 'hidden'}
                variants={FadeInOutVariants}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                {/* Actions Section - Left */}
                <Box
                  flex={{ base: 'none', md: 'none', lg: '1', '3xl': '1' }}
                  width={{ base: 'full', md: 'full', lg: 'auto', '3xl': 'auto' }}
                  paddingLeft={rem(8)}
                >
                  <ActionsSection category={fullCategoryData} />
                </Box>

                {/* Projects Section - Right */}
                <Box
                  flex={{ base: 'none', md: 'none', lg: '1', '3xl': '1' }}
                  width={{ base: 'full', md: 'full', lg: 'auto', '3xl': 'auto' }}
                  paddingRight={rem(8)}
                >
                  <ProjectsSection category={fullCategoryData} />
                </Box>
              </MotionFlex>
            </MotionFlex>
          )}
        </Flex>
        {isHeroImageLoaded && (
          <FullCaptureListFAB
            onClick={() => {
              navigate(RoutesList.WeeklyCapturePage);
            }}
            buttonProps={{
              left: '45%',
              translateX: '-45%',
            }}
            innerText={tWeeklyCapture('buttons.viewFullCategoryList')}
            chevronLeft={true}
          />
        )}
      </Flex>
    </DashboardLayout>
  );
};

export default CategorySpotlight;
